import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSSRData } from '../components/SSRWrapper';
import { API_CONFIG, fetchWithTimeout, getAuthHeaders, processApiResponse } from '../config/api';

import '../styles/Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const { logout, getToken, getUserInfo } = useAuth();
  const { initialData } = useSSRData();

  // Usar dados SSR como estado inicial
  const [indications, setIndications] = useState(initialData.indications || []);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewIndicationForm, setShowNewIndicationForm] = useState(false);
  const [showEditIndicationForm, setShowEditIndicationForm] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [editingIndication, setEditingIndication] = useState(null);
  const [deletingIndication, setDeletingIndication] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    cpf: ''
  });
  const [editFormData, setEditFormData] = useState({
    nome: '',
    telefone: '',
    cpf: ''
  });

  const [submittingNew, setSubmittingNew] = useState(false);
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [submittingDelete, setSubmittingDelete] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Estados para mensagens de notificação
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success' // 'success' ou 'error'
  });

  const userInfoItems = getUserInfo();

  // Função para mostrar notificações
  const showNotification = (message, type = 'success') => {
    setNotification({
      show: true,
      message,
      type
    });

    // Auto-hide após 5 segundos
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  // Buscar dados apenas se não vieram do SSR
  useEffect(() => {
    if (!initialData.indications || initialData.indications.length === 0) {
      fetchIndications();
    }
  }, []);

  const fetchIndications = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const headers = getAuthHeaders(token);

      const userId = userInfoItems.id;
      const requestBody = {
        id_users: userId
      };

      const response = await fetchWithTimeout(API_CONFIG.GET_INDICATIONS_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      const data = await processApiResponse(response);

      if (data.indications && Array.isArray(data.indications)) {
        setIndications(data.indications);
      } else {
        setIndications([]);
      }
    } catch (error) {
      console.error('Erro ao carregar indicações:', error);
      const errorMessage = error.message || error.data?.message || error.data?.error || 'Erro ao carregar indicações. Tente novamente.';
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleEditIndication = (indication) => {
    setEditingIndication(indication);
    setEditFormData({
      nome: indication.name || '',
      telefone: indication.telephone || '',
      cpf: indication.cpf || ''
    });
    setShowEditIndicationForm(true);
  };

  const handleDeleteClick = (indication) => {
    setDeletingIndication(indication);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {

    if (!deletingIndication || submittingDelete) return;

    setSubmittingDelete(true);

    try {
      await handleDeleteIndication(deletingIndication);
      setShowDeleteConfirmation(false);
      setDeletingIndication(null);
    } finally {
      setSubmittingDelete(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setDeletingIndication(null);
  };

  const handleDeleteIndication = async (indication) => {

    try {
      const token = getToken();
      const headers = getAuthHeaders(token);

      const response = await fetchWithTimeout(`${API_CONFIG.DELETE_INDICATIONS_URL}/${indication.id}`, {
        method: 'DELETE',
        headers
      });

      const data = await processApiResponse(response);

      showNotification(data.message || 'Indicação excluída com sucesso!', 'success');
      // Recarregar indicações
      fetchIndications();
    } catch (error) {
      console.error('Erro ao excluir indicação:', error);
      const errorMessage = error.message || error.data?.message || error.data?.error || 'Erro ao excluir indicação. Tente novamente.';
      showNotification(errorMessage, 'error');
    }
  };

  const handleCloseEditModal = () => {
    setShowEditIndicationForm(false);
    setEditingIndication(null);
    setEditFormData({
      nome: '',
      telefone: '',
      cpf: ''
    });
  };

  const updateStatus = async () => {
    setLoading(true);

    if (updatingStatus) return;

    setUpdatingStatus(true);
    try {
      const token = getToken();
      const headers = getAuthHeaders(token);

      const userId = userInfoItems.id;
      const requestBody = {
        id_users: userId
      };

      const response = await fetchWithTimeout(API_CONFIG.UPDATE_STATUS_INDICATIONS_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      const data = await processApiResponse(response);

      console.log('Status atualizado:', data);
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      const errorMessage = error.message || error.data?.message || error.data?.error || 'Erro ao atualizar status. Tente novamente.';
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
      setUpdatingStatus(false);
    }
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (submittingEdit) return;

    // Validação básica
    if (!editFormData.nome || !editFormData.telefone || !editFormData.cpf) {
      showNotification('Por favor, preencha todos os campos.', 'error');
      return;
    }

    setSubmittingEdit(true);

    try {
      const token = getToken();
      const headers = getAuthHeaders(token);

      const indicationData = {
        indication: {
          id: editingIndication.id,
          name: editFormData.nome,
          cpf: editFormData.cpf
        }
      };

      const response = await fetchWithTimeout(API_CONFIG.UPDATE_INDICATIONS_URL, {
        method: 'PUT',
        headers,
        body: JSON.stringify(indicationData)
      });

      const data = await processApiResponse(response);

      showNotification(data.message || 'Indicação editada com sucesso!', 'success');
      handleCloseEditModal();
      // Recarregar indicações
      fetchIndications();
    } catch (error) {
      console.error('Erro ao editar indicação:', error);
      const errorMessage = error.message || error.data?.message || error.data?.error || 'Erro ao editar indicação. Tente novamente.';
      showNotification(errorMessage, 'error');
    } finally {
      setSubmittingEdit(false);
    }
  };

  const formatCpfInput = (value) => {
    const cpfClean = value.replace(/\D/g, '');

    if (cpfClean.length <= 11) {
      if (cpfClean.length <= 3) {
        return cpfClean;
      } else if (cpfClean.length <= 6) {
        return `${cpfClean.slice(0, 3)}.${cpfClean.slice(3)}`;
      } else if (cpfClean.length <= 9) {
        return `${cpfClean.slice(0, 3)}.${cpfClean.slice(3, 6)}.${cpfClean.slice(6)}`;
      } else {
        return `${cpfClean.slice(0, 3)}.${cpfClean.slice(3, 6)}.${cpfClean.slice(6, 9)}-${cpfClean.slice(9, 11)}`;
      }
    }

    return value;
  };

  const formatPhoneInput = (value) => {
    const phoneClean = value.replace(/\D/g, '');
    if (phoneClean.length <= 11) {
      if (phoneClean.length <= 2) {
        return phoneClean;
      } else if (phoneClean.length <= 7) {
        return `(${phoneClean.slice(0, 2)}) ${phoneClean.slice(2)}`;
      } else {
        return `(${phoneClean.slice(0, 2)}) ${phoneClean.slice(2, 7)}-${phoneClean.slice(7, 11)}`;
      }
    }
    return value;
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'telefone') {
      // Formatação do telefone
      const phoneValue = value.replace(/\D/g, '');
      let formattedPhone = phoneValue;

      if (phoneValue.length >= 2) {
        formattedPhone = `(${phoneValue.slice(0, 2)}) ${phoneValue.slice(2)}`;
      }
      if (phoneValue.length >= 7) {
        formattedPhone = `(${phoneValue.slice(0, 2)}) ${phoneValue.slice(2, 7)}-${phoneValue.slice(7, 11)}`;
      }

      setEditFormData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
    } else if (name === 'cpf') {
      // Formatação do CPF
      const cpfValue = value.replace(/\D/g, '');
      let formattedCpf = cpfValue;

      if (cpfValue.length >= 3) {
        formattedCpf = `${cpfValue.slice(0, 3)}.${cpfValue.slice(3)}`;
      }
      if (cpfValue.length >= 6) {
        formattedCpf = `${cpfValue.slice(0, 3)}.${cpfValue.slice(3, 6)}.${cpfValue.slice(6)}`;
      }
      if (cpfValue.length >= 9) {
        formattedCpf = `${cpfValue.slice(0, 3)}.${cpfValue.slice(3, 6)}.${cpfValue.slice(6, 9)}-${cpfValue.slice(9, 11)}`;
      }

      setEditFormData(prev => ({
        ...prev,
        [name]: formattedCpf
      }));
    } else {
      setEditFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submittingNew) return;

    // Validação básica
    if (!formData.nome || !formData.telefone || !formData.cpf) {
      showNotification('Por favor, preencha todos os campos.', 'error');
      return;
    }

    setSubmittingNew(true);

    try {
      const token = getToken();
      const headers = getAuthHeaders(token);

      const indicationData = {
        indication: {
          id_users: userInfoItems.id,
          name: formData.nome,
          cpf: formData.cpf,
          telephone: formData.telefone
        }
      };

      const response = await fetchWithTimeout(API_CONFIG.CREATE_INDICATIONS_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify(indicationData)
      });

      const data = await processApiResponse(response);
      console.log(data)

      showNotification(data.message || 'Indicação criada com sucesso!', 'success');
      setFormData({ nome: '', telefone: '', cpf: '' });
      setShowNewIndicationForm(false);
      fetchIndications();
    } catch (error) {
      console.error('Erro ao enviar indicação:', error);
      const errorMessage = error.message || error.data?.message || error.data?.error || 'Erro de conexão. Tente novamente.';
      showNotification(errorMessage, 'error');
    } finally {
      setSubmittingNew(false);
    }
  };

  const copyEmailToClipboard = (email) => {
    if (!email) return;
    navigator.clipboard.writeText(email).then(() => {
      showNotification('E-mail copiado para a área de transferência!');
    }).catch(() => {
      showNotification('Não foi possível copiar o e-mail.', 'error');
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Data não informada';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const formatPhone = (phone) => {
    if (!phone) return 'Telefone não informado';
    const phoneClean = phone.replace(/\D/g, '');
    if (phoneClean.length === 11) {
      return `(${phoneClean.slice(0, 2)}) ${phoneClean.slice(2, 7)}-${phoneClean.slice(7)}`;
    } else if (phoneClean.length === 10) {
      return `(${phoneClean.slice(0, 2)}) ${phoneClean.slice(2, 6)}-${phoneClean.slice(6)}`;
    }
    return phone;
  };

  const filteredIndications = indications.filter(indication => {
    const name = indication.name || "";
    const telefone = indication.telefone || "";
    const cpf = indication.cpf || "";

    return (
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      telefone.includes(searchTerm) ||
      cpf.includes(searchTerm)
    );
  });

  const totalIndications = indications.length;

  return (
    <div className="dashboard-container user">
      {/* Notificação */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <div className="notification-content">
            <span className="notification-message">{notification.message}</span>
            <button
              className="notification-close"
              onClick={() => setNotification(prev => ({ ...prev, show: false }))}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <div className="logo-container">
            <img alt='Amigo Indica Amigo' src='/images/dashboard/logo-aia-azul.svg' className='logo' width={49} height={39} />
          </div>
        </div>

        <div className="header-center">
          <div className="nav-tabs">
            <div className="nav-tab active">
              <img alt='' src='/images/dashboard/svg/home_storage.svg' fill="currentColor"/>
              <h3>Minhas Indicações</h3>
            </div>
          </div>
          <div className="search-bar">
            <input
              type="text"
              placeholder="Pesquisar"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <img alt='' src='/images/dashboard/svg/search.svg' fill="currentColor" />
          </div>
        </div>

        <div className="header-right">
          <button className="create-user-btn top-nav-btn" onClick={() => setShowNewIndicationForm(true)}>
            <img src='/images/dashboard/svg/new_window.svg' alt='Criar Usuário' className="top-nav-btn-icon-desktop" />
            <img src='/images/dashboard/svg/new_window_gray.svg' alt='Criar Usuário' className="top-nav-btn-icon-mobile" />
            <span className="top-nav-btn-text">Nova Indicação</span>
          </button>
          <button className="logout-btn top-nav-btn" onClick={handleLogout}>
            <img src='/images/dashboard/svg/move_item.svg' alt='Criar Usuário' className="top-nav-btn-icon-desktop" />
            <span className="top-nav-btn-text">Sair</span>
          </button>
        </div>
      </header>

      {/* User Profile Section */}
      <section className="profile-section">
        <div className="profile-content">
          <div className="profile-left">
            {userInfoItems?.avatar ? (
              <img
                src={userInfoItems.avatar}
                alt={userInfoItems.nome || "Usuário"}
                className="avatar-img"
              />
            ) : (
              <img alt='' src='/images/dashboard/standing-6.png' className="avatar-img" />
            )}
            <div className="user-name-wrapper">
                <h2 className="user-name">{userInfoItems?.nome || "Usuário"}</h2>
              </div>
              <div className="user-details">
              <div className="detail-item ra">
                <img alt='' src='/images/dashboard/svg/backpack.svg' fill="currentColor" />
                  <h3 className="detail-item-text">
                  Login:
                  <span> {userInfoItems?.ra || "0000000"}</span>
                </h3>
              </div>
              <div className="detail-item email" onClick={() => copyEmailToClipboard(userInfoItems?.email)} style={{ cursor: 'pointer' }}>
                <img alt='' src='/images/dashboard/svg/mail.svg' fill="currentColor" />
                <h3 className="detail-item-text">
                  E-mail:
                  <span> {userInfoItems?.email || "email@exemplo.com"}</span>
                </h3>
              </div>
            </div>
          </div>

          <div className="profile-right">
            <div className="search-bar mobile-search">
              <input
                type="text"
                placeholder="Pesquisar usuários..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <img alt='' src='/images/dashboard/svg/search_mobile.svg'/>
            </div>
            <div className="metric-card">
              <div className="metric-label">Total de indicações</div>
              <div className="metric-value">{totalIndications}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-update-status">
        <div>
          <button className="update-btn" disabled={updatingStatus} onClick={() => updateStatus()}>
            <img src='/images/dashboard/svg/update_arrow.svg' alt='Atualizar Status' className="top-nav-btn-icon-desktop" />
            <img src='/images/dashboard/svg/update_arrow.svg' alt='Atualizar Status' className="top-nav-btn-icon-mobile" />
            <span>{updatingStatus ? 'Atualizando...' : 'Atualizar Status'}</span>
          </button>
        </div>
      </section>

      {/* Main Content */}
      <main className="dashboard-main">
        <div className="table-container">
          <h3 className='table-title'>Lista de Indicações</h3>
          <table className="indications-table">
            <thead>
              <tr>
                <th className='th-name'>Nome Indicado</th>
                <th className='th-telephone'>Telefone</th>
                <th className='th-cpf'>CPF</th>
                <th className='th-status'>Status</th>
                <th className='th-date'>Data</th>
                <th className='th-actions'>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="loading-cell">
                    <div className="loading-spinner"></div>
                    Carregando indicações...
                  </td>
                </tr>
              ) : filteredIndications.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-cell">
                    Nenhuma indicação encontrada
                  </td>
                </tr>
              ) : (
                filteredIndications.map((indication) => (
                  <tr key={indication.id} className="user-row">
                    <td>{indication.name}</td>
                    <td>{formatPhone(indication.telephone)}</td>
                    <td className='indication-cpf'>{indication.cpf}</td>
                    <td className='indication-status'>
                      <span
                        className={`status-badge ${indication.status === 'DISCOUNT_RELEASED' ? 'status-done'
                          : indication.status === 'SENT' ? 'status-sent'
                            : indication.status === 'IN_SERVICE' ? 'status-service'
                              : indication.status === 'PURCHASE_CONFIRMED' ? 'status-purchase-confirmed'
                                : indication.status === 'EXPIRED' ? 'status-expired '
                                  : 'status-default'
                          }`}
                      >
                        {indication.status === 'DISCOUNT_RELEASED'
                          ? 'Desconto por indicação liberado'
                          : indication.status === 'SENT'
                            ? 'Indicação enviada'
                            : indication.status === 'IN_SERVICE'
                              ? 'Indicação em atendimento'
                              : indication.status === 'PURCHASE_CONFIRMED'
                                ? 'Compra confirmada'
                                : indication.status === 'EXPIRED'
                                  ? 'Indicação expirada'
                                  : 'Desconto por indicação em processamento'}
                      </span>
                    </td>
                    <td className="user-date">{formatDate(indication.created_at)}</td>
                    <td className="action-buttons">
                      <button
                        className="action-btn edit-btn"
                        title="Editar"
                        onClick={() => handleEditIndication(indication)}
                      >
                        <img alt='' src='/images/dashboard/svg/edit.svg' fill="currentColor" />
                      </button>
                      <button
                        className="action-btn delete-btn"
                        title="Excluir"
                        onClick={() => handleDeleteClick(indication)}>
                        <img alt='' src='/images/dashboard/svg/delete_sweep.svg' fill="currentColor" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* New Indication Modal */}
      {showNewIndicationForm && (
        <div className="modal-overlay">
          <div className="modal-content new-indication-modal">
            <div className="modal-header">
              <button className="go-back-btn" onClick={() => setShowNewIndicationForm(false)}>
                <img src="/images/dashboard/svg/arrow_back.svg" alt="" className="go-back-btn-icon" />
              </button>
              <h3 className="modal-title">NOVA INDICAÇÃO</h3>
              <button
                className="close-btn"
                onClick={() => setShowNewIndicationForm(false)}
              >
                ✕
              </button>
            </div>

            <div className="new-indication-content">
              <div className="new-indication-left">
                <img alt='' src='/images/dashboard/conections-group.png' />
              </div>
              <div className="new-indication-right">
                <div className="description-text">
                  <p>Preencha os dados básicos do amigo que você quer indicar para estudar na UNIGUAÇU!</p>
                </div>
                <form onSubmit={handleSubmit} className="new-indication-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="nome">NOME</label>
                      <input
                        type="text"
                        id="nome"
                        name="nome"
                        value={formData.nome}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            nome: e.target.value
                          }));
                        }}
                        placeholder="Digite aqui o nome..."
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="telefone">TELEFONE</label>
                      <input
                        type="text"
                        id="telefone"
                        name="telefone"
                        value={formData.telefone}
                        maxLength={15}
                        onChange={(e) => {
                          const formatted = formatPhoneInput(e.target.value);
                          setFormData(prev => ({
                            ...prev,
                            telefone: formatted
                          }));
                        }}
                        placeholder="(11) 99999-9999"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="cpf">CPF</label>
                      <input
                        type="text"
                        id="cpf"
                        name="cpf"
                        value={formData.cpf}
                        maxLength={14}
                        onChange={(e) => {
                          const formattedCpf = formatCpfInput(e.target.value);
                          setFormData(prev => ({
                            ...prev,
                            cpf: formattedCpf
                          }));
                        }}
                        placeholder="000.000.000-00"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-actions">
                      <button type="submit" className="save-btn" disabled={submittingNew}>
                        Salvar
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Indication Modal */}
      {showEditIndicationForm && editingIndication && (
        <div className="modal-overlay">
          <div className="modal-content edit-modal">
            <div className="modal-header">
              <button className="go-back-btn" onClick={handleCloseEditModal}>
                <img src="/images/dashboard/svg/arrow_back.svg" alt="" className="go-back-btn-icon" />
              </button>
              <h3 className="modal-title">EDITAR INDICAÇÃO</h3>
              <button
                className="close-btn"
                onClick={handleCloseEditModal}
              >
                ✕
              </button>
            </div>

            <div className="edit-modal-content">
              <div className="edit-modal-left">
                <img alt='' src="/images/dashboard/undraw_filter_v54.svg" />
              </div>

              <div className="edit-modal-right">
                <form onSubmit={handleEditSubmit} className="edit-indication-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-nome">NOME</label>
                      <input
                        type="text"
                        id="edit-nome"
                        name="nome"
                        value={editFormData.nome}
                        onChange={handleEditInputChange}
                        placeholder="Digite o nome completo"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="edit-cpf">CPF</label>
                      <input
                        type="text"
                        id="edit-cpf"
                        name="cpf"
                        disabled
                        value={editFormData.cpf}
                        // onChange={handleEditInputChange}
                        placeholder="000.000.000-00"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-telefone">TELEFONE</label>
                      <div className="input-with-icon">
                        <input
                          type="text"
                          id="edit-telefone"
                          name="telefone"
                          disabled
                          value={editFormData.telefone}
                          // onChange={handleEditInputChange}
                          placeholder="(11) 99999-9999"
                          required
                        />
                        <img alt='' src='/images/dashboard/svg/lock.svg' fill="currentColor" />
                      </div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="save-btn" disabled={submittingEdit}>
                      Salvar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && deletingIndication && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirmation-modal">
            <div className="modal-header">
              <button className="go-back-btn" onClick={handleDeleteCancel}>
                <img src="/images/dashboard/svg/arrow_back.svg" alt="" className="go-back-btn-icon" />
              </button>
              <h3 className="modal-title">CONFIRMAR EXCLUSÃO</h3>
              <button
                className="close-btn"
                onClick={handleDeleteCancel}
              >
                ✕
              </button>
            </div>

            <div className="delete-confirmation-content">
              <div className="warning-icon">
                ⚠️
              </div>

              <div className="confirmation-message">
                <h4>Tem certeza que deseja excluir esta indicação?</h4>
                <p>Esta ação não pode ser desfeita.</p>
              </div>

              <div className="indication-details">
                <div className="detail-item">
                  <strong>Nome:</strong> {deletingIndication.name}
                </div>
                <div className="detail-item">
                  <strong>Telefone:</strong> {formatPhone(deletingIndication.telephone)}
                </div>
                <div className="detail-item">
                  <strong>CPF:</strong> {deletingIndication.cpf}
                </div>
              </div>

              <div className="confirmation-actions">
                <button
                  type="button"
                  className="cancel-delete-btn"
                  onClick={handleDeleteCancel}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="confirm-delete-btn"
                  disabled={submittingDelete}
                  onClick={handleDeleteConfirm}
                >
                  {submittingDelete ? 'Excluindo...' : 'Sim, Excluir'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
